import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { formatTime } from '../../format/helpers';
import { usePlayback } from '../../state/playback';
import { RULER_H, TICK_STEPS } from './constants';
import { useTimelineCtx } from './context';

export function Ruler({
  viewport,
  width,
}: {
  viewport: [number, number];
  width: number;
}) {
  const { pps, controls } = useTimelineCtx();
  const loop = usePlayback((s) => s.loop);
  const step = TICK_STEPS.find((s) => s * pps >= 64) ?? 3600;
  const minor = step / 4;
  const showMinor = minor * pps >= 10;

  const [v0, v1] = viewport;
  const t0 = Math.max(0, Math.floor(v0 / pps / step) * step);
  const t1 = (v1 + 100) / pps;

  const ticks: JSX.Element[] = [];
  for (let t = t0; t <= t1; t += step) {
    ticks.push(
      <div className="tl-tick" key={`M${t}`} style={{ left: t * pps }}>
        <span>{formatTime(t)}</span>
      </div>,
    );
    if (showMinor) {
      for (let m = t + minor; m < t + step - 1e-9; m += minor) {
        ticks.push(<div className="tl-tick-minor" key={`m${m}`} style={{ left: m * pps }} />);
      }
    }
  }

  // Coalesce scrub seeks to one per animation frame: pointermove fires far more
  // often than we can usefully re-seek video, and the frame-fallback path makes
  // per-frame updates cheap. The last position always wins; on pointer-up we
  // flush so the final settle seek (which lets the live video re-buffer) lands.
  const pendingT = useRef<number | null>(null);
  const rafId = useRef(0);
  const flushSeek = useCallback(() => {
    rafId.current = 0;
    if (pendingT.current !== null) {
      controls.seek(pendingT.current);
      pendingT.current = null;
    }
  }, [controls]);
  const scheduleSeek = useCallback(
    (t: number) => {
      pendingT.current = t;
      if (!rafId.current) rafId.current = requestAnimationFrame(flushSeek);
    },
    [flushSeek],
  );
  useEffect(
    () => () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    },
    [],
  );

  function timeFromEvent(e: ReactPointerEvent<HTMLDivElement>): number {
    const rect = e.currentTarget.getBoundingClientRect();
    return (e.clientX - rect.left) / pps;
  }

  return (
    <div
      className="tl-ruler"
      style={{ width, height: RULER_H }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        scheduleSeek(timeFromEvent(e));
      }}
      onPointerMove={(e) => {
        if (e.buttons & 1) scheduleSeek(timeFromEvent(e));
      }}
      onPointerUp={() => {
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
          flushSeek();
        }
      }}
    >
      {ticks}
      {loop && loop.in !== null && loop.out !== null && loop.out > loop.in && (
        <div
          className="tl-loop-band"
          style={{ left: loop.in * pps, width: (loop.out - loop.in) * pps }}
        />
      )}
      {loop?.in != null && <div className="tl-loop-edge" style={{ left: loop.in * pps }} />}
      {loop?.out != null && <div className="tl-loop-edge" style={{ left: loop.out * pps }} />}
    </div>
  );
}

export function Playhead() {
  const { pps } = useTimelineCtx();
  const t = usePlayback((s) => s.currentTime);
  return (
    <div className="tl-playhead" style={{ left: t * pps }}>
      <div className="tl-playhead-cap" />
    </div>
  );
}
